import { motion } from 'framer-motion';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  MessageSquare,
  Mail,
  Eye,
  Edit,
  Trash2,
  Archive,
  ArchiveRestore,
  Search,
  Filter,
  Tag,
  Plus,
} from 'lucide-react';
import ContactsPipelineBoard from './ContactsPipelineBoard';
import CompassActivityWidget from './CompassActivityWidget';

/**
 * Presentational Contacts tab. All state and mutating actions are owned by
 * the parent `AdminDashboardPage` and passed in as props. This keeps the tab
 * self-contained visually while the parent remains the single source of truth
 * for the shared dialogs (View / Edit / Delete / Import) that live outside
 * the tab.
 */
export default function ContactsTab({
  token,
  contactView,
  setContactView,
  stats,
  filteredContacts,
  allTags,
  selectedContacts,
  searchTerm,
  setSearchTerm,
  subjectFilter,
  setSubjectFilter,
  tagFilter,
  setTagFilter,
  showArchived,
  setShowArchived,
  subjectLabels,
  formatDate,
  setShowTagManagement,
  setBulkTagOperation,
  setShowBulkTagDialog,
  setSelectedContacts,
  handleSelectAll,
  handleSelectContact,
  setSelectedContact,
  setEditingContact,
  setDeleteConfirm,
  handleArchive,
  handleUnarchive,
  onContactUpdated,
}) {
  return (
    <>
      {/* View toggle: List vs Pipeline Board */}
      <div className="bg-white rounded-xl border p-3 flex items-center justify-between">
        <div>
          <h2 className="font-editorial text-2xl">Contacts</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage the pipeline of every artisan and event host who&apos;s reached out.
          </p>
        </div>
        <div className="inline-flex rounded-full bg-muted p-1" data-testid="contacts-view-toggle">
          <button
            onClick={() => setContactView('list')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              contactView === 'list'
                ? 'bg-white shadow-sm text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="contacts-view-list"
          >
            List
          </button>
          <button
            onClick={() => setContactView('board')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              contactView === 'board'
                ? 'bg-white shadow-sm text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="contacts-view-board"
          >
            Pipeline Board
          </button>
        </div>
      </div>

      {contactView === 'board' ? (
        <>
          <CompassActivityWidget token={token} />
          <ContactsPipelineBoard token={token} onContactUpdated={onContactUpdated} />
        </>
      ) : (
        <>
          <CompassActivityWidget token={token} />
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 border shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Contacts</p>
                    <p className="text-2xl font-semibold">{stats.total_contacts}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-6 border shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Unread</p>
                    <p className="text-2xl font-semibold">{stats.unread_contacts}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl p-6 border shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Archive className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Archived</p>
                    <p className="text-2xl font-semibold">{stats.archived_contacts}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-6 border shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Newsletter Subs</p>
                    <p className="text-2xl font-semibold">{stats.active_newsletter_subscribers}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl border p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="contact-search"
                />
              </div>
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="subject-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {Object.entries(subjectLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="tag-filter">
                  <Tag className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={showArchived ? 'default' : 'outline'}
                onClick={() => setShowArchived(!showArchived)}
                data-testid="toggle-archived"
              >
                <Archive className="w-4 h-4 mr-2" />
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowTagManagement(true)}
                data-testid="manage-tags-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                Manage Tags
              </Button>
            </div>
          </div>

          {/* Bulk Action Toolbar */}
          {selectedContacts.size > 0 && (
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-medium text-primary">
                  {selectedContacts.size} contact{selectedContacts.size > 1 ? 's' : ''} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedContacts(new Set())}
                >
                  Clear selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setBulkTagOperation('add');
                    setShowBulkTagDialog(true);
                  }}
                  data-testid="bulk-add-tags"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Tags
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBulkTagOperation('replace');
                    setShowBulkTagDialog(true);
                  }}
                  data-testid="bulk-replace-tags"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Replace Tags
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBulkTagOperation('remove');
                    setShowBulkTagDialog(true);
                  }}
                  data-testid="bulk-remove-tags"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove Tags
                </Button>
              </div>
            </div>
          )}

          {/* Contacts Table */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-4 text-sm font-medium text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={filteredContacts.length > 0 && selectedContacts.size === filteredContacts.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Subject</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Tags</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                        No contacts found
                      </td>
                    </tr>
                  ) : (
                    filteredContacts.map((contact) => (
                      <tr
                        key={contact.id}
                        className={`border-b hover:bg-muted/30 transition-colors ${!contact.read ? 'bg-blue-50/50' : ''} ${contact.archived ? 'opacity-60' : ''} ${selectedContacts.has(contact.id) ? 'bg-primary/5' : ''}`}
                        data-testid={`contact-row-${contact.id}`}
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedContacts.has(contact.id)}
                            onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid={`select-contact-${contact.id}`}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {!contact.read && (
                              <span className="w-2 h-2 rounded-full bg-blue-500" title="Unread" />
                            )}
                            {contact.archived && (
                              <Badge variant="secondary">Archived</Badge>
                            )}
                            {contact.unsubscribed && (
                              <Badge
                                variant="outline"
                                className={
                                  contact.unsubscribed_reason === 'hard_bounce'
                                    ? 'border-red-400 text-red-700 bg-red-50'
                                    : contact.unsubscribed_reason === 'spam_complaint'
                                    ? 'border-orange-500 text-orange-700 bg-orange-50'
                                    : 'border-amber-400 text-amber-700 bg-amber-50'
                                }
                                title={
                                  (contact.unsubscribed_reason
                                    ? `Reason: ${contact.unsubscribed_reason.replace('_', ' ')}. `
                                    : '') +
                                  (contact.unsubscribed_at
                                    ? `Unsubscribed ${new Date(contact.unsubscribed_at).toLocaleDateString()}`
                                    : 'Unsubscribed')
                                }
                                data-testid={`contact-unsubscribed-badge-${contact.id}`}
                              >
                                {contact.unsubscribed_reason === 'hard_bounce' && 'Bounced'}
                                {contact.unsubscribed_reason === 'spam_complaint' && 'Complained'}
                                {!contact.unsubscribed_reason && 'Unsubscribed'}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`font-medium ${!contact.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {contact.name}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">{contact.email}</td>
                        <td className="px-4 py-4">
                          <Badge variant="outline">{subjectLabels[contact.subject] || contact.subject}</Badge>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {contact.tags && contact.tags.length > 0 ? (
                              contact.tags.map((tag, idx) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="text-xs cursor-pointer hover:bg-primary hover:text-white"
                                  onClick={() => setTagFilter(tag)}
                                >
                                  {tag}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {formatDate(contact.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedContact(contact)}
                              data-testid={`view-contact-${contact.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingContact(contact)}
                              data-testid={`edit-contact-${contact.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {contact.archived ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnarchive(contact.id)}
                                data-testid={`unarchive-contact-${contact.id}`}
                              >
                                <ArchiveRestore className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchive(contact.id)}
                                data-testid={`archive-contact-${contact.id}`}
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleteConfirm(contact)}
                              data-testid={`delete-contact-${contact.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
